import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Snackbar,
  Alert,
  InputBase,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import { themeColors } from '../styles/theme';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  pubName: string;
  pubAddress?: string;
  currentUrl: string;
}

const ShareModal = ({ open, onClose, pubName, pubAddress, currentUrl }: ShareModalProps) => {
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [shareApiAvailable, setShareApiAvailable] = useState(false);
  
  useEffect(() => {
    // Check if Web Share API is available
    setShareApiAvailable(!!navigator.share);
    
    // Create QR code URL using QR Server API
    const encodedUrl = encodeURIComponent(currentUrl);
    setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?data=${encodedUrl}&size=200x200&margin=10&format=svg`);
  }, [currentUrl]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopied(true);
  };

  const handleSystemShare = async () => {
    if (!navigator.share) return;
    
    try {
      await navigator.share({
        title: `${pubName} - Suntap`,
        text: `Check out ${pubName} on Suntap!`,
        url: currentUrl
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleWhatsAppShare = () => {
    const message = `Check out ${pubName} on Suntap!`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message + " " + currentUrl)}`;
    window.open(whatsappUrl, '_blank');
  };
  
  const handleCloseCopiedAlert = () => {
    setCopied(false);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            maxWidth: { xs: '90%', sm: 400 },
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            position: 'relative'
          }
        }}
      >
        {/* Close button in top right */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: themeColors.darkBackground,
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        
        {/* Suntap Branding */}
        <Box 
          sx={{ 
            textAlign: 'center',
            mt: 2,
            mb: 0
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: "'DM Sans', sans-serif",
              color: '#FC5208',
              fontSize: { xs: '1.4rem', sm: '1.6rem' },
              textAlign: 'center',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Suntap
          </Typography>
        </Box>
        
        <DialogTitle 
          sx={{ 
            fontFamily: "'DM Sans', sans-serif",
            color: themeColors.darkBackground,
            textAlign: 'center',
            pt: 0,
            pb: pubAddress ? 0 : 1,
          }}
        >
          {pubName}
        </DialogTitle>
        
        {pubAddress && (
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center',
              color: themeColors.darkBackground,
              mb: 1,
              px: 3
            }}
          >
            {pubAddress}
          </Typography>
        )}
        
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            width: '100%'
          }}>
            {/* QR Code */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2, 
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                display: 'inline-block',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}
            >
              <img
                src={qrCodeUrl}
                alt="QR Code"
                width={160}
                height={160}
                style={{ display: 'block' }}
              />
            </Paper>
            
            {/* Copy Link Section */}
            <Box sx={{ width: '100%' }}>
              <Paper
                elevation={2}
                sx={{
                  p: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <InputBase
                  sx={{ 
                    ml: 1, 
                    flex: 1, 
                    fontSize: '0.9rem',
                    color: themeColors.darkBackground
                  }}
                  value={currentUrl}
                  readOnly
                />
                <Tooltip title="Copy link">
                  <IconButton 
                    color="primary"
                    sx={{ p: '10px', color: themeColors.blue }}
                    onClick={copyToClipboard}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Box>
            
            {/* Share Buttons - Simplified styling */}
            <Box sx={{ 
              display: 'flex', 
              width: '100%', 
              gap: 2,
              mb: 0
            }}>
              {/* WhatsApp Share Button - Simplified */}
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsAppShare}
                sx={{
                  flex: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  py: 1,
                  fontWeight: 600,
                  bgcolor: '#25D366',
                  '&:hover': {
                    bgcolor: '#1ea952',
                  }
                }}
              >
                WhatsApp
              </Button>
              
              {/* System Share Button - Simplified */}
              {shareApiAvailable && (
                <Button
                  variant="contained"
                  startIcon={<ShareIcon />}
                  onClick={handleSystemShare}
                  sx={{
                    flex: 1,
                    borderRadius: '10px',
                    textTransform: 'none',
                    py: 1,
                    fontWeight: 600,
                    background: `linear-gradient(90deg, ${themeColors.orange} 0%, ${themeColors.mainYellow} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(90deg, ${themeColors.orange} 0%, ${themeColors.orange} 100%)`,
                    }
                  }}
                >
                  Share
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={handleCloseCopiedAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseCopiedAlert} 
          severity="success" 
          variant="filled"
          sx={{ borderRadius: '8px' }}
        >
          Link copied!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareModal;